import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Save, Printer, FileText, Upload, File as FileIcon, X, Zap, Loader2, CheckCircle2 } from "lucide-react";
import { Toaster, toast } from "../ui/sonner";
import { api } from "../../utils/auth";

export default function TelaahStaffModal({ open, onOpenChange, sub, user }) {
  const [dumasFile, setDumasFile] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState(null);

  // Load saved data when modal opens
  useEffect(() => {
    if (open && sub) {
      const saved = localStorage.getItem(`telaah_staff_${sub.id}`);
      if (saved) {
        setReport(JSON.parse(saved));
      } else {
        setReport(null);
      }
      setDumasFile(null);
      setIsGenerating(false);
    }
  }, [open, sub]);

  const handleSave = () => {
    if (report) {
      const reportWithTimestamp = {
        ...report,
        createdAt: new Date().toISOString(),
        kategori: sub.kategori || "Umum",
      };
      localStorage.setItem(`telaah_staff_${sub.id}`, JSON.stringify(reportWithTimestamp));
      toast.success("Hasil Telaahaan Staff berhasil disimpan ke draft");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDumasFile(file);
      // Konversi file ke base64 dan simpan ke localStorage agar bisa view/unduh
      const reader = new FileReader();
      reader.onload = () => {
        const uploadedFiles = JSON.parse(localStorage.getItem('uploadedFiles') || '[]');
        const newFile = {
          id: Date.now().toString(),
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString(),
          uploadedBy: user?.name || "Unknown",
          dataUrl: reader.result,
        };
        uploadedFiles.unshift(newFile);
        localStorage.setItem('uploadedFiles', JSON.stringify(uploadedFiles));
      };
      reader.readAsDataURL(file);
      toast.success("Surat Dumas berhasil diunggah dan disimpan");
    }
  };

  const removeFile = () => {
    setDumasFile(null);
  };

  const handleGenerate = async () => {
    if (!dumasFile) {
      toast.error("Harap unggah Surat Dumas (PDF) terlebih dahulu untuk dianalisis.");
      return;
    }
    
    setIsGenerating(true);
    try {
      const formData = new FormData();
      formData.append("file", dumasFile);

      const res = await api.post("/telaah/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setReport(res.data);
      toast.success("Dokumen berhasil dianalisis oleh Claude 3 Opus!");
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail || "Gagal menganalisis dokumen. Pastikan koneksi internet stabil dan OpenRouter API Key sudah benar.";
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  const renderValue = (val) => {
    if (val === null || val === undefined) return "—";
    if (typeof val === "object") {
      // If it has specific keys we expect from AI
      if (val.layak_ditindaklanjuti !== undefined) {
        return (
          <div className="space-y-2">
            <div className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${val.layak_ditindaklanjuti ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {val.layak_ditindaklanjuti ? 'LAYAK DITINDAKLANJUTI' : 'TIDAK LAYAK'}
            </div>
            <p>{val.alasan || val.deskripsi || ""}</p>
          </div>
        );
      }
      return JSON.stringify(val);
    }
    return String(val);
  };

  const buildHtmlContent = () => {
    if (!report) return "";
    const dateStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    
    return `
      <html>
        <head>
          <title>Nota Dinas - ${sub?.nama}</title>
          <style>
            body { font-family: 'Arial', sans-serif; padding: 20px; color: #000; line-height: 1.4; font-size: 11pt; }
            .kop-surat { display: flex; align-items: center; border-bottom: 4px double #000; padding-bottom: 10px; margin-bottom: 15px; }
            .logo { width: 70px; height: auto; margin-right: 20px; }
            .kop-text { text-align: center; flex-grow: 1; margin-right: 70px; }
            .kop-text h2 { margin: 0; font-size: 14pt; font-weight: bold; }
            .kop-text h1 { margin: 0; font-size: 18pt; font-weight: bold; }
            .kop-text p { margin: 2px 0; font-size: 9pt; }
            
            .title { text-align: center; text-decoration: underline; font-weight: bold; font-size: 13pt; margin: 20px 0 10px 0; }
            
            .meta-table { width: 100%; margin-bottom: 20px; }
            .meta-table td { vertical-align: top; padding: 2px 0; }
            
            .section-title { font-weight: bold; margin-top: 15px; margin-bottom: 5px; }
            .content { text-align: justify; margin-bottom: 10px; }
            .list-item { margin-left: 25px; margin-bottom: 5px; }
            
            .signature-container { margin-top: 40px; display: flex; justify-content: space-between; }
            .sig-box { width: 45%; text-align: center; }
            .sig-name { margin-top: 60px; font-weight: bold; text-decoration: underline; }
            
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="kop-surat">
            <img src="https://upload.wikimedia.org/wikipedia/commons/2/29/Logo_Kabupaten_Tasikmalaya.png" class="logo" />
            <div class="kop-text">
              <h2>PEMERINTAH DAERAH KABUPATEN TASIKMALAYA</h2>
              <h1>INSPEKTORAT DAERAH</h1>
              <p>JALAN SUKAPURA II KOMPLEK PERKANTORAN SINGAPARNA</p>
              <p>Telepon/Fax : (0265) 2553000 / (0265) 2553000</p>
              <p>Website : http://www.inspektorat.tasikmalayakab.go.id</p>
              <p>email : <span style="color: blue; text-decoration: underline;">inspektorat@tasikmalayakab.go.id</span></p>
            </div>
          </div>
          
          <div class="title">NOTA DINAS</div>
          
          <table class="meta-table">
            <tr><td width="15%">Kepada Yth</td><td width="2%">:</td><td>Inspektur Daerah Kabupaten Tasikmalaya</td></tr>
            <tr><td>Dari</td><td>:</td><td>Tim Penelaah</td></tr>
            <tr><td>Perihal</td><td>:</td><td>Telaahan terkait ${report.sumber_telaahan || sub?.nama}</td></tr>
            <tr><td>Tanggal</td><td>:</td><td>${dateStr}</td></tr>
          </table>
          <hr style="border: 0; border-top: 1px solid #000; margin-bottom: 20px;" />

          <div class="content">
            Berdasarkan ${report.sumber_telaahan || 'Laporan Pengaduan Masyarakat'}, berikut disampaikan hasil telaahan staff:
          </div>

          <div class="section-title">I. Sumber Telaahan</div>
          <div class="content">${report.sumber_telaahan || '-'}</div>

          <div class="section-title">II. Dasar Hukum Yang Berpengaruh Terhadap Persoalan Pokok</div>
          <div class="content">
            <div class="list-item">1. UU Nomor 23 Tahun 2014 tentang Pemerintahan Daerah.</div>
            <div class="list-item">2. UU Nomor 30 Tahun 2014 tentang Administrasi Pemerintahan.</div>
            <div class="list-item">3. Peraturan Pemerintah Nomor 60 Tahun 2008 tentang Sistem Pengendalian Intern Pemerintah.</div>
            <div class="list-item">4. Peraturan Pemerintah Nomor 12 Tahun 2017 tentang Pembinaan dan Pengawasan Penyelenggaraan Pemerintahan Daerah.</div>
            <div class="list-item">5. Peraturan Pemerintah Nomor 38 Tahun 2016 tentang Tuntutan Ganti Kerugian Negara/Daerah.</div>
            <div class="list-item">6. Permendagri Nomor 133 Tahun 2018 tentang Penyelesaian tuntutan Ganti Kerugian Daerah.</div>
            <div class="list-item">7. Peraturan Bupati Nomor 14 Tahun 2022 tentang Tugas dan fungsi Inspektorat Daerah.</div>
            <div class="list-item">8. Nota Kesepakatan antara Pemda Kab. Tasikmalaya dan Kejaksaan Negeri Nomor 100.3.7/2115/Tapem/2025.</div>
          </div>

          <div class="section-title">III. Materi Telaahan</div>
          <div class="content" style="white-space: pre-wrap;">${report.materi_telaahan || '-'}</div>

          <div class="section-title">IV. Analisa</div>
          <div class="content" style="white-space: pre-wrap;">${report.analisa || '-'}</div>

          <div class="section-title">V. Kesimpulan</div>
          <div class="content">${typeof report.kesimpulan === 'object' ? (report.kesimpulan.alasan || JSON.stringify(report.kesimpulan)) : report.kesimpulan}</div>

          <div class="section-title">VI. Saran</div>
          <div class="content">${report.saran || '-'}</div>

          <div class="content" style="margin-top: 30px;">
            Demikian telaahan ini kami sampaikan sebagai bahan lebih lanjut.
          </div>

          <div class="signature-container">
            <div class="sig-box">
              <p>Koordinator</p>
              <p>Inspektur Pembantu Khusus</p>
              <p class="sig-name">${user?.role === 'irban' ? user.name : 'ARA SUNDARA, S.Sos'}</p>
              <p>NIP. 19710914 199203 1 003</p>
            </div>
            <div class="sig-box">
              <p>Tim Pemeriksa:</p>
              <p style="text-align: left; margin-left: 20%; margin-top: 10px;">1. ${user?.name} (Penelaah)</p>
              <p style="text-align: left; margin-left: 20%;">2. ............................</p>
              <p style="text-align: left; margin-left: 20%;">3. ............................</p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const handleExportPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return toast.error("Pop-up diblokir. Izinkan pop-up untuk PDF.");
    
    const htmlContent = buildHtmlContent();
    const script = `
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
            window.close();
          }, 500);
        }
      </script>
    `;
    printWindow.document.write(htmlContent + script);
    printWindow.document.close();
  };

  const handleExportWord = () => {
    const htmlContent = buildHtmlContent();
    
    // Add Microsoft Word xmlns format wrapper for better styling retention
    const wordDoc = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>Telaahan Staff</title></head>
      <body>${htmlContent}</body>
      </html>
    `;

    const blob = new Blob([wordDoc], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Telaahan_Staff_${sub?.id || 'dumas'}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Dokumen Word berhasil diunduh");
  };

  const resetReport = () => {
    setReport(null);
  };

  if (!sub) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#f8fafd]" data-testid="telaah-staff-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="font-display text-lg text-[#1e3a5f]">Tool Telaahan Staff Otomatis</DialogTitle>
              <DialogDescription className="text-[13px]">
                {sub.nama} - {sub.kategori}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-2">
          
          {!report && !isGenerating && (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#1e3a5f]/20 bg-white py-12 px-6 shadow-sm">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <FileIcon className="h-7 w-7" />
              </div>
              <h3 className="mb-1 text-base font-semibold text-slate-800">Mulai Analisis Surat Dumas</h3>
              <p className="mb-6 max-w-sm text-center text-[12px] leading-relaxed text-slate-500">
                Sistem akan membaca isi file pengaduan, lalu mengekstrak elemen 5W+1H dan memberikan kesimpulan secara otomatis.
              </p>

              {dumasFile ? (
                <div className="flex flex-col items-center gap-4 w-full max-w-sm">
                  <div className="flex w-full items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <div className="flex items-center gap-2 text-emerald-800">
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="text-[12px] font-semibold truncate max-w-[200px]">{dumasFile.name}</span>
                    </div>
                    <button onClick={removeFile} className="text-emerald-600 hover:text-emerald-800">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <Button onClick={handleGenerate} className="w-full bg-[#1e3a5f] hover:bg-[#152a46] text-white">
                    <Zap className="mr-2 h-4 w-4" /> Mulai Analisis Dokumen
                  </Button>
                </div>
              ) : (
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-[#1e3a5f] hover:bg-[#152a46] px-6 py-2.5 text-[13px] font-medium text-white shadow-sm transition-colors">
                  <Upload className="h-4 w-4" /> Pilih File Surat Dumas
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileUpload} />
                </label>
              )}
            </div>
          )}

          {isGenerating && (
            <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-16 shadow-sm">
              <Loader2 className="mb-4 h-8 w-8 animate-spin text-[#1e3a5f]" />
              <h3 className="text-sm font-semibold text-slate-800">Sistem sedang membaca dokumen...</h3>
              <p className="mt-1 text-[12px] text-slate-500">Menganalisis indikasi 5W+1H dan merumuskan kesimpulan</p>
            </div>
          )}

          {report && !isGenerating && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 space-y-4">
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="border-b border-gray-100 bg-gray-50 px-5 py-3 flex items-center justify-between">
                  <h3 className="text-[13px] font-bold text-slate-800 uppercase tracking-wide">Hasil Telaahan Staff (Nota Dinas)</h3>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Siap Cetak
                    </span>
                  </div>
                </div>
                
                <div className="p-6 space-y-6 max-h-[500px] overflow-y-auto">
                  <section>
                    <h4 className="text-[12px] font-bold text-[#1e3a5f] uppercase mb-2">I. Sumber Telaahan</h4>
                    <div className="text-[13px] text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-100">
                      {renderValue(report.sumber_telaahan)}
                    </div>
                  </section>

                  <section>
                    <h4 className="text-[12px] font-bold text-[#1e3a5f] uppercase mb-2">III. Materi Telaahan</h4>
                    <div className="text-[13px] text-slate-700 whitespace-pre-wrap">
                      {renderValue(report.materi_telaahan)}
                    </div>
                  </section>

                  <section>
                    <h4 className="text-[12px] font-bold text-[#1e3a5f] uppercase mb-2">IV. Analisa</h4>
                    <div className="text-[13px] text-slate-700 whitespace-pre-wrap">
                      {renderValue(report.analisa)}
                    </div>
                  </section>

                  <section>
                    <h4 className="text-[12px] font-bold text-[#1e3a5f] uppercase mb-2">V. Kesimpulan</h4>
                    <div className="text-[13px] font-semibold text-slate-800 bg-blue-50 p-3 rounded-lg border border-blue-100">
                      {renderValue(report.kesimpulan)}
                    </div>
                  </section>

                  <section>
                    <h4 className="text-[12px] font-bold text-[#1e3a5f] uppercase mb-2">VI. Saran</h4>
                    <div className="text-[13px] text-slate-700">
                      {renderValue(report.saran)}
                    </div>
                  </section>
                </div>
              </div>
            </div>
          )}

        </div>

        <DialogFooter className="flex-wrap gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:justify-between -mx-6 -mb-6 rounded-b-lg mt-4">
          <div className="flex gap-2">
            {report && (
              <>
                <Button variant="outline" onClick={handleExportPDF} className="gap-1.5 border-gray-300 text-slate-700 hover:text-red-600 bg-white">
                  <Printer className="h-4 w-4" /> Cetak PDF
                </Button>
                <Button variant="outline" onClick={handleExportWord} className="gap-1.5 border-gray-300 text-slate-700 hover:text-blue-600 bg-white">
                  <FileText className="h-4 w-4" /> Unduh Word
                </Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Tutup</Button>
            {report && (
              <>
                <Button variant="outline" onClick={resetReport} className="text-slate-600 bg-white border-gray-300">
                  Analisis Ulang
                </Button>
                <Button onClick={handleSave} className="gap-1.5 bg-[#1e3a5f] hover:bg-[#152a46] text-white">
                  <Save className="h-4 w-4" /> Simpan Draft
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
